<script setup lang="ts">
/**
 * A whole page frame: NxPageHeader on top, the page content under it, all in
 * one padded column that scrolls. `flush` stops the column from scrolling so
 * a table page can scroll inside its own table instead. Dialogs go into the
 * `dialogs` slot, outside the scrolling column.
 *
 * Takes the same props and slots as NxPageHeader (`leading`, `actions`,
 * `below-title` — the header's default slot —, `toolbar`) plus the body in
 * the default slot.
 */
import NxPageHeader from './NxPageHeader.vue';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    kicker?: string;
    error?: string | null;
    hint?: string | null;
    /** No scrolling in the column; the content (a table) scrolls itself. */
    flush?: boolean;
  }>(),
  { subtitle: '', kicker: '', error: null, hint: null, flush: false }
);
</script>

<template>
  <div class="nx-page-shell" data-testid="nx-page-shell">
    <main class="nx-page-shell__main" :class="{ 'is-flush': props.flush }">
      <NxPageHeader
        :title="props.title"
        :subtitle="props.subtitle"
        :kicker="props.kicker"
        :error="props.error"
        :hint="props.hint"
      >
        <template v-if="$slots.leading" #leading><slot name="leading" /></template>
        <template v-if="$slots['below-title']" #default><slot name="below-title" /></template>
        <template v-if="$slots.actions" #actions><slot name="actions" /></template>
        <template v-if="$slots.toolbar" #toolbar><slot name="toolbar" /></template>
      </NxPageHeader>
      <slot />
    </main>
    <slot name="dialogs" />
  </div>
</template>

<style scoped>
.nx-page-shell {
  display: flex;
  width: 100%;
  height: 100%;
  min-height: 0;
}
.nx-page-shell__main {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--nxt-space-md);
  min-width: 0;
  min-height: 0;
  padding: var(--nxt-space-lg) var(--nxt-space-xl) var(--nxt-space-xl);
  overflow: auto;
}
.nx-page-shell__main.is-flush {
  overflow: hidden;
}
</style>
